// Radvis sparning av uppdragsbudgetar (projects/{id}.budgets).
//
// Budgetarna är en lista i projektdokumentet. Varje budget, resurs, aktivitet,
// aktivitetsrad (resourceLines) och licens har ett stabilt id. diffBudgetar
// räknar fram vad som ändrats sedan senast sparade läge, och applyBudgetar
// tillämpar det på dokumentets aktuella läge i en transaktion. Två personer
// som ändrar olika rader i samma budget skriver alltså inte över varandra.
// Minsta enhet är en rad: en aktivitetsrad med sina månader, en resurs, en
// licens eller ett budgetfält (namn, period …).
//
// Ofullständiga resurs- och licensrader sparas inte förrän de har det minsta
// som behövs (resurs: person eller rollnamn, licens: plattform/namn).

import { same, diffRows, applyRows } from '../../utils/radSync';

const LISTOR = ['resources', 'activities', 'licenses'];

export function resursKomplett(r) {
  return !!(r.userId || (r.name || '').trim());
}

export function licensKomplett(l) {
  return !!(l.name || '').trim();
}

const KOMPLETT = { resources: resursKomplett, licenses: licensKomplett };

/** Id:n på ofullständiga rader i en budget: { resources: [...], licenses: [...] }. */
export function ofullstandigaRader(budget) {
  const ut = {};
  Object.entries(KOMPLETT).forEach(([lista, ok]) => {
    ut[lista] = (budget?.[lista] || []).filter(r => !ok(r)).map(r => r.id);
  });
  return ut;
}

/**
 * Det läge som får sparas: ofullständiga rader som aldrig sparats tas bort,
 * och en sparad rad som blivit ofullständig behåller sitt sparade läge.
 */
export function sparbaraBudgetar(baslinje, budgetar) {
  const basById = new Map((baslinje || []).map(b => [b.id, b]));
  return budgetar.map(b => {
    const bas = basById.get(b.id);
    const ut = { ...b };
    Object.entries(KOMPLETT).forEach(([lista, ok]) => {
      if (!b[lista]) return;
      const sparade = new Map((bas?.[lista] || []).map(r => [r.id, r]));
      ut[lista] = b[lista]
        .map(r => (ok(r) ? r : sparade.get(r.id)))
        .filter(Boolean);
    });
    return ut;
  });
}

// Aktiviteter: ändrade aktivitetsrader skrivs radvis inne i aktiviteten.
function diffAktiviteter(lista, fore, efter) {
  const d = diffRows(lista, fore, efter, { radKvittens: true });
  if (!d) return null;
  const foreById = new Map(fore.map(a => [a.id, a]));
  Object.entries(d.change.updates).forEach(([id, andrat]) => {
    if (!('resourceLines' in andrat)) return;
    const rader = diffRows(`${lista}.${id}.rader`,
      foreById.get(id)?.resourceLines || [], andrat.resourceLines || [], { radKvittens: true });
    if (rader) andrat.resourceLines = { __rader: rader.change };
    else delete andrat.resourceLines;
  });
  return d;
}

function applyAktiviteter(remote, change) {
  const radUpdates = {};
  const updates = {};
  Object.entries(change.updates).forEach(([id, andrat]) => {
    const { resourceLines, ...ovrigt } = andrat;
    if (resourceLines?.__rader) radUpdates[id] = resourceLines.__rader;
    else if (resourceLines !== undefined) ovrigt.resourceLines = resourceLines;
    if (Object.keys(ovrigt).length) updates[id] = ovrigt;
  });
  return applyRows(remote, { ...change, updates }).map(a =>
    radUpdates[a?.id] ? { ...a, resourceLines: applyRows(a.resourceLines, radUpdates[a.id]) } : a);
}

/**
 * Vad som skiljer `efter` från `fore` (båda listor av budgetar).
 * → { tillagda, borttagna, andrade: { [id]: { falt, listor } }, ordning, keys, tom }
 */
export function diffBudgetar(fore, efter) {
  const foreById = new Map((fore || []).map(b => [b.id, b]));
  const efterIds = new Set(efter.map(b => b.id));
  const tillagda = efter.filter(b => !foreById.has(b.id));
  const borttagna = (fore || []).filter(b => !efterIds.has(b.id)).map(b => b.id);
  const andrade = {};
  const keys = [];
  tillagda.forEach(b => keys.push(`budget.${b.id}`));
  borttagna.forEach(id => keys.push(`budget.${id}`));

  efter.forEach(b => {
    const f = foreById.get(b.id);
    if (!f) return;
    const falt = {};
    new Set([...Object.keys(f), ...Object.keys(b)]).forEach(k => {
      if (k === 'id' || LISTOR.includes(k)) return;
      if (!same(f[k], b[k])) {
        falt[k] = b[k] === undefined ? null : b[k];
        keys.push(`budget.${b.id}.${k}`);
      }
    });
    const listor = {};
    LISTOR.forEach(lista => {
      const namn = `budget.${b.id}.${lista}`;
      const d = lista === 'activities'
        ? diffAktiviteter(namn, f[lista] || [], b[lista] || [])
        : diffRows(namn, f[lista] || [], b[lista] || [], { radKvittens: true });
      if (d) { listor[lista] = d.change; keys.push(...d.keys); }
    });
    if (Object.keys(falt).length || Object.keys(listor).length) andrade[b.id] = { falt, listor };
  });

  const ordningFore = (fore || []).filter(b => efterIds.has(b.id)).map(b => b.id);
  const ordningEfter = efter.filter(b => foreById.has(b.id)).map(b => b.id);
  const ordning = same(ordningFore, ordningEfter) ? null : ordningEfter;

  const tom = !tillagda.length && !borttagna.length && !Object.keys(andrade).length && !ordning;
  return { tillagda, borttagna, andrade, ordning, keys, tom };
}

/** Tillämpar diffBudgetar på budgetlistans aktuella läge i databasen. */
export function applyBudgetar(remote, diff) {
  const added = diff.tillagda.map((row, i) => ({ row, after: i === 0 ? null : diff.tillagda[i - 1].id }));
  // Nya budgetar läggs sist, i den ordning de skapades
  let budgetar = applyRows(remote, { removed: diff.borttagna, added: [], updates: {}, order: diff.ordning });
  added.forEach(({ row }) => { if (!budgetar.some(b => b?.id === row.id)) budgetar.push(row); });
  budgetar = budgetar.map(b => {
    const d = diff.andrade[b?.id];
    if (!d) return b;
    const ut = { ...b, ...d.falt };
    Object.entries(d.listor).forEach(([lista, change]) => {
      ut[lista] = lista === 'activities'
        ? applyAktiviteter(b[lista], change)
        : applyRows(b[lista], change);
    });
    return ut;
  });
  return budgetar;
}
